import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Alert,
  Table,
} from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getSaldosRemitos } from "../helpers/rutaReportes";

import ModalOrdenPagoRemito from "../components/ModalOrdenPagoRemito";
import ModalOrdenCobroRemitos from "../components/ModalOrdenCobroRemitos";
import ModalAjusteCtaCte from "../components/ModalAjusteCtaCte";

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayYYYYMMDD = () => new Date().toISOString().slice(0, 10);

const sortES = (a, b) =>
  (a || "").localeCompare(b || "", "es", { sensitivity: "base" });

const getEntityId = (x) =>
  x?._id || x?.id || x?.clienteId || x?.proveedorId || x?.cliente?._id || x?.proveedor?._id;

export default function ReporteSaldosRemitos() {
  const [fecha, setFecha] = useState(todayYYYYMMDD());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Modal OP desde reporte
  const [showModalOP, setShowModalOP] = useState(false);
  const [proveedorOP, setProveedorOP] = useState(null);

  // Modal OC desde reporte
  const [showModalOC, setShowModalOC] = useState(false);
  const [clienteOC, setClienteOC] = useState(null);

  const [showAjuste, setShowAjuste] = useState(false);

  const consultar = async () => {
    setErr("");
    setLoading(true);

    try {
      const r = await getSaldosRemitos(fecha);

      if (!r?.ok) {
        throw new Error(r?.error || r?.msg || "No se pudo cargar el reporte");
      }

      setData(r);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const clientes = useMemo(() => {
    return (data?.clientes || [])
      .map((c) => ({
        ...c,
        saldo: Number(c.saldoA || 0) + Number(c.saldoB || 0) + Number(c.saldoFlete || 0),
      }))
      .sort((a, b) => sortES(a?.razonsocial, b?.razonsocial));
  }, [data]);

  const proveedores = useMemo(() => {
    return (data?.proveedores || [])
      .map((p) => ({
        ...p,
        saldo: Number(p.saldoA || 0) + Number(p.saldoB || 0),
      }))
      .sort((a, b) => sortES(a?.razonsocial, b?.razonsocial));
  }, [data]);

  const tot = useMemo(() => {
    const totalClientes = clientes.reduce((sum, c) => sum + c.saldo, 0);
    const totalProveedores = proveedores.reduce((sum, p) => sum + p.saldo, 0);
    return {
      clientes: totalClientes,
      proveedores: totalProveedores,
      neto: totalClientes - totalProveedores,
    };
  }, [clientes, proveedores]);

  const abrirOCCliente = (c) => {
    const id = getEntityId(c);

    if (!id) {
      alert("No se encontró el ID del cliente en el reporte.");
      console.log("Cliente sin ID:", c);
      return;
    }

    setClienteOC({
      _id: id,
      razonsocial: c?.razonsocial || c?.cliente || "",
      cliente: c?.razonsocial || c?.cliente || "",
    });

    setShowModalOC(true);
  };

  const abrirOPProveedor = (p) => {
    const id = getEntityId(p);

    if (!id) {
      alert("No se encontró el ID del proveedor en el reporte.");
      console.log("Proveedor sin ID:", p);
      return;
    }

    setProveedorOP({
      _id: id,
      razonsocial: p?.razonsocial || p?.proveedor || "",
      proveedor: p?.razonsocial || p?.proveedor || "",
    });

    setShowModalOP(true);
  };

  const abrirCtaCteEntidad = (tipoEntidad, entidad) => {
  const id = getEntityId(entidad);

  if (!id) {
    alert("No se encontró el ID para abrir la cuenta corriente.");
    console.log("Entidad sin ID:", entidad);
    return;
  }

  const hasta = fecha || todayYYYYMMDD();
  const year = String(hasta).slice(0, 4);
  const desde = `${year}-01-01`;

  const params = new URLSearchParams({
    tipo: tipoEntidad,
    id,
    desde,
    hasta,
  });

  window.location.href = `/informectacte?${params.toString()}`;
};

  const exportarPDF = () => {
    if (!data) return;

    const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const title = "Reporte de Saldos (Remitos)";
    const subtitle = `Fecha de corte: ${data?.fechaCorte || fecha}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(title, 14, 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subtitle, 14, 20);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(13, 110, 253);
    doc.text(`Total Clientes: $ ${fmtARS(tot.clientes)}`, 14, 27);
    doc.text(`Total Proveedores: $ ${fmtARS(tot.proveedores)}`, 14, 32);
    doc.text(`Neto (Cli - Prov): $ ${fmtARS(tot.neto)}`, 14, 37);
    doc.setFontSize(10);
    doc.text(`Clientes - Total: $ ${fmtARS(tot.clientes)}`, 14, 45);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
      startY: 48,
      head: [["Cliente", "Saldo A", "Saldo B", "Saldo Flete", "Saldo"]],
      body: clientes.map((x) => [
        x.razonsocial || "",
        fmtARS(x.saldoA),
        fmtARS(x.saldoB),
        fmtARS(x.saldoFlete),
        fmtARS(x.saldo),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
    });

    const y = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : 60;
    const needsNewPage = y > 260;

    if (needsNewPage) doc.addPage();

    const y2 = needsNewPage ? 14 : y;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(13, 110, 253);
    doc.text(`Proveedores - Total: $ ${fmtARS(tot.proveedores)}`, 14, y2);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
      startY: y2 + 3,
      head: [["Proveedor", "Saldo A", "Saldo B", "Saldo"]],
      body: proveedores.map((x) => [
        x.razonsocial || "",
        fmtARS(x.saldoA),
        fmtARS(x.saldoB),
        fmtARS(x.saldo),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
      },
    });

    doc.save(`reporte-saldos-remitos-${data?.fechaCorte || fecha}.pdf`);
  };

  return (
    <Container fluid="md" className="py-3" translate="no">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
            <div style={{ minWidth: 240 }}>
              <Form.Group>
                <Form.Label className="mb-1">Fecha de corte</Form.Label>
                <Form.Control
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="d-flex gap-2">
              <Button
                onClick={consultar}
                disabled={loading}
                variant={loading ? "secondary" : "primary"}
              >
                <span>{loading ? "Consultando..." : "Consultar"}</span>
              </Button>

              <Button
                variant="secondary"
                onClick={exportarPDF}
                disabled={!data || loading}
              >
                Exportar PDF
              </Button>

              <Button
                variant="warning"
                onClick={() => setShowAjuste(true)}
                style={{
                  fontWeight: 700,
                  color: "#111",
                  border: "1px solid #b36b00",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                }}
              >
                + Ajuste Cta Cte
              </Button>
</div>
          </div>

          {err ? (
            <Alert className="mt-3 mb-0" variant="danger">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      {!data ? (
        <div className="mt-3 text-muted">
          Elegí una fecha y tocá <b>“Consultar”</b>.
        </div>
      ) : (
        <>
          <Row className="mt-3 g-2">
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <div className="text-primary" style={{ fontSize: 11 }}>
                    Total Clientes
                  </div>
                  <div className="fw-bold text-primary" style={{ fontSize: 18, lineHeight: 1.2 }}>
                    $ {fmtARS(tot.clientes)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <div className="text-primary" style={{ fontSize: 11 }}>
                    Total Proveedores
                  </div>
                  <div className="fw-bold text-primary" style={{ fontSize: 18, lineHeight: 1.2 }}>
                    $ {fmtARS(tot.proveedores)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <div className="text-primary" style={{ fontSize: 11 }}>
                    Neto (Cli - Prov)
                  </div>
                  <div className="fw-bold text-primary" style={{ fontSize: 18, lineHeight: 1.2 }}>
                    $ {fmtARS(tot.neto)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>


          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h5 className="mb-0">Clientes</h5>
                <span className="text-primary fw-bold" style={{ fontSize: 14 }}>
                  Total: $ {fmtARS(tot.clientes)}
                </span>
              </div>

              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{ tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 120, textAlign: "center" }}>Acciones</th>
                      <th>Razón social</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo A</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo B</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo Flete</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {clientes.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center">
                          Sin registros para la fecha consultada.
                        </td>
                      </tr>
                    ) : (
                      clientes.map((c) => (
                        <tr key={c.id || c._id || c.razonsocial}>
                          <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                            <Button
                              size="sm"
                              variant="success"
                              title="Nueva Orden de Cobro"
                              onClick={() => abrirOCCliente(c)}
                              disabled={Number(c?.saldo || 0) === 0}
                              style={{
                                minWidth: 42,
                                padding: "2px 8px",
                                fontWeight: 700,
                              }}
                            >
                              OC
                            </Button>{" "}

                            <Button
                              size="sm"
                              variant="outline-primary"
                              title="Ver cuenta corriente"
                              onClick={() => abrirCtaCteEntidad("CLIENTE", c)}
                              style={{
                                minWidth: 42,
                                padding: "2px 8px",
                                fontWeight: 700,
                              }}
                            >
                              CTA
                            </Button>
                          </td>
                          <td>{c.razonsocial}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldoA)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldoB)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldoFlete)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(c.saldo)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h5 className="mb-0">Proveedores</h5>
                <span className="text-primary fw-bold" style={{ fontSize: 14 }}>
                  Total: $ {fmtARS(tot.proveedores)}
                </span>
              </div>

              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{ tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 120, textAlign: "center" }}>Acciones</th>
                      <th>Razón social</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo A</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo B</th>
                      <th style={{ width: 140, textAlign: "right" }}>Saldo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {proveedores.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Sin registros para la fecha consultada.
                        </td>
                      </tr>
                    ) : (
                      proveedores.map((p) => (
                        <tr key={p.id || p._id || p.razonsocial}>
                          <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                            <Button
                              size="sm"
                              variant="success"
                              title="Nueva Orden de Pago"
                              onClick={() => abrirOPProveedor(p)}
                              disabled={Number(p?.saldo || 0) === 0}
                              style={{
                                minWidth: 42,
                                padding: "2px 8px",
                                fontWeight: 700,
                              }}
                            >
                              OP
                            </Button>{" "}

                            <Button
                              size="sm"
                              variant="outline-primary"
                              title="Ver cuenta corriente"
                              onClick={() => abrirCtaCteEntidad("PROVEEDOR", p)}
                              style={{
                                minWidth: 42,
                                padding: "2px 8px",
                                fontWeight: 700,
                              }}
                            >
                              CTA
                            </Button>
                          </td>

                          <td>{p.razonsocial}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(p.saldoA)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(p.saldoB)}</td>
                          <td style={{ textAlign: "right" }}>$ {fmtARS(p.saldo)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

        </>
      )}

      <ModalOrdenPagoRemito
        show={showModalOP}
        onHide={(saved) => {
          setShowModalOP(false);
          setProveedorOP(null);

          if (saved) consultar();
        }}
        proveedor={proveedorOP}
        cliente={null}
        remitosPreseleccionados={[]}
      />

      <ModalOrdenCobroRemitos
        show={showModalOC}
        onHide={(saved) => {
          setShowModalOC(false);
          setClienteOC(null);

          if (saved) consultar();
        }}
        cliente={clienteOC}
        proveedor={null}
        preseleccion={[]}
      />

      <ModalAjusteCtaCte
        show={showAjuste}
        onHide={(saved) => {
          setShowAjuste(false);

          if (saved) {
            console.log("✅ Ajuste Cta Cte guardado correctamente");
          }
        }}
      />
    </Container>
  );
}